import { isDefined } from "@clipboard-health/util-ts";
import { useGeofenceContext } from "@src/appV2/Location/geofence/useGeofence";
import { useWorkerLocations } from "@src/appV2/redesign/GeoLocation/useWorkerLocations";
import { isAtLeast12HoursAway } from "@src/appV2/redesign/utils/isAtLeast12HoursAway";
import { parseISO } from "date-fns";
import { useMemo } from "react";

import type { ArrivalContext, ArrivalState } from "../types";

interface UseArrivalStateParams {
  shiftStart: string;
  currentDate: Date;
}

/**
 * Clean, single-responsibility hook for determining arrival state
 * Based on simple rules:
 * 1. Inside geofence = arrived
 * 2. >12 hours away = not ready to travel
 * 3. <12 hours + current location = traveling
 * 4. <12 hours + no current location = ready to travel (from home)
 * 5. No location data = unknown
 */
export function useArrivalState({
  shiftStart,
  currentDate,
}: UseArrivalStateParams): ArrivalContext {
  const { inside } = useGeofenceContext();
  const locations = useWorkerLocations();

  return useMemo(() => {
    const shiftStartTime = parseISO(shiftStart);
    const isMoreThan12HoursAway = isAtLeast12HoursAway(shiftStartTime, currentDate);

    // Rule 1: Inside geofence = arrived (highest priority)
    if (inside) {
      return {
        state: "arrived" as ArrivalState,
        estimatedTravelTime: undefined,
        travelFromLocation: undefined,
      };
    }

    // Rule 2: >12 hours away = not ready to travel
    if (isMoreThan12HoursAway) {
      return {
        state: "not-ready-to-travel" as ArrivalState,
        estimatedTravelTime: undefined,
        travelFromLocation: undefined,
      };
    }

    // Rule 3: <12 hours + current location = traveling
    if (isDefined(locations.deviceLocation)) {
      return {
        state: "traveling" as ArrivalState,
        estimatedTravelTime: undefined, // Will be calculated by travel time hook
        travelFromLocation: {
          lat: locations.deviceLocation.lat,
          lng: locations.deviceLocation.lng,
          type: "current" as const,
        },
      };
    }

    // Rule 4: <12 hours + no current location = ready to travel from home
    if (isDefined(locations.homeLocation)) {
      return {
        state: "ready-to-travel" as ArrivalState,
        estimatedTravelTime: undefined, // Will be calculated by travel time hook
        travelFromLocation: {
          lat: locations.homeLocation.lat,
          lng: locations.homeLocation.lng,
          type: "home" as const,
        },
      };
    }

    // Rule 5: No location data = unknown
    return {
      state: "unknown" as ArrivalState,
      estimatedTravelTime: undefined,
      travelFromLocation: undefined,
    };
  }, [inside, shiftStart, currentDate, locations.deviceLocation, locations.homeLocation]);
}
